import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { MarketplaceBreadcrumb } from '@/components/common/marketplace-breadcrumb'
import { ImageGallery, SellerCard, ContactCard, CarCard } from '@/components/marketplace'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SectionHeading } from '@/components/common'
import { ShieldCheck, Heart, Share2, Calendar, Gauge, Fuel, Car, Cpu, Zap, CheckCircle2, Loader2, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react'
import { appRoutes } from '@/constants'
import { NotFoundPage } from '@/pages/not-found-page'
import { useCarDetails, useMarketplaceListings } from '@/modules/cars/hooks/use-cars'
import { CarChatDrawer } from '@/modules/chat/components/car-chat-drawer'

export function CarDetailsPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data, isLoading, error } = useCarDetails(slug || '')
  const { data: marketplaceData } = useMarketplaceListings({ limit: 10 })
  const sliderRef = useRef<HTMLDivElement>(null)
  const [isChatOpen, setIsChatOpen] = useState(false)

  const scrollSlider = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const scrollAmount = direction === 'left' ? -340 : 340
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  if (isLoading) {
    return (
      <div className="p-24 text-center flex flex-col items-center justify-center space-y-3">
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading vehicle details...</p>
      </div>
    )
  }

  if (error || !data?.listing) {
    return <NotFoundPage />
  }

  const car = data.listing

  const FALLBACK_SIMILAR_CARS = [
    {
      id: 'sim-1',
      title: '2024 Porsche 911 GT3 RS',
      slug: 'porsche-911-gt3-rs',
      askingPrice: 24500000,
      manufactureYear: 2023,
      mileageKm: 4200,
      fuelType: 'PETROL',
      transmission: 'AUTOMATIC',
      condition: 'PRE-OWNED',
      locationCity: 'Mumbai',
      locationState: 'Maharashtra',
      images: ['https://images.unsplash.com/photo-1611821064430-0d40291d0f0d?auto=format&fit=crop&w=800&q=80'],
      brand: { name: 'Porsche' },
    },
    {
      id: 'sim-2',
      title: '2023 BMW M4 Competition',
      slug: 'bmw-m4-competition',
      askingPrice: 9840000,
      manufactureYear: 2023,
      mileageKm: 8500,
      fuelType: 'PETROL',
      transmission: 'AUTOMATIC',
      condition: 'USED',
      locationCity: 'Delhi',
      locationState: 'NCR',
      images: ['https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?auto=format&fit=crop&w=800&q=80'],
      brand: { name: 'BMW' },
    },
    {
      id: 'sim-3',
      title: '2022 Mercedes-AMG GT R',
      slug: 'mercedes-amg-gt-r',
      askingPrice: 18500000,
      manufactureYear: 2022,
      mileageKm: 12000,
      fuelType: 'PETROL',
      transmission: 'AUTOMATIC',
      condition: 'CERTIFIED',
      locationCity: 'Bangalore',
      locationState: 'Karnataka',
      images: ['https://images.unsplash.com/photo-1544829099-b9a0c07fad1a?auto=format&fit=crop&w=800&q=80'],
      brand: { name: 'Mercedes-Benz' },
    },
    {
      id: 'sim-4',
      title: '2023 Audi RS7 Sportback',
      slug: 'audi-rs7-sportback',
      askingPrice: 14500000,
      manufactureYear: 2023,
      mileageKm: 6100,
      fuelType: 'PETROL',
      transmission: 'AUTOMATIC',
      condition: 'PRE-OWNED',
      locationCity: 'Pune',
      locationState: 'Maharashtra',
      images: ['https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?auto=format&fit=crop&w=800&q=80'],
      brand: { name: 'Audi' },
    },
  ]

  const liveOtherCars = (marketplaceData?.items || []).filter((item: any) => item.id !== car.id && item.slug !== car.slug)

  const relatedCars = (data.related && data.related.length > 0)
    ? data.related
    : liveOtherCars.length > 0
    ? liveOtherCars
    : FALLBACK_SIMILAR_CARS

  const galleryImages = Array.isArray(car.images)
    ? car.images.map((img: any) => (typeof img === 'string' ? img : img.url))
    : ['https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?auto=format&fit=crop&w=1200&q=80']

  const formattedPrice = `₹${Number(car.askingPrice).toLocaleString('en-IN')}`

  const sellerInfo = {
    id: car.user?.id || car.userId,
    name: car.user ? `${car.user.firstName} ${car.user.lastName}` : 'Verified Merchant',
    role: car.user?.role === 'DEALER' ? 'Certified Dealership' : 'Private Seller',
    type: car.user?.role === 'DEALER' ? 'Certified Dealership' : 'Private Seller',
    location: `${car.locationCity}, ${car.locationState}`,
    phone: car.user?.phone || '+91 98765 43210',
    email: car.user?.email || 'seller@dealercommerce.com',
    avatar: car.user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=250&q=80',
    rating: 4.9,
    verified: true,
  }

  const specsList = [
    { label: 'Manufacture Year', value: car.manufactureYear, icon: Calendar },
    { label: 'Mileage', value: `${car.mileageKm.toLocaleString('en-IN')} km`, icon: Gauge },
    { label: 'Fuel Type', value: car.fuelType, icon: Fuel },
    { label: 'Transmission', value: car.transmission, icon: Car },
    { label: 'Drive Type', value: car.driveType || 'FWD', icon: Cpu },
    { label: 'Condition', value: car.condition, icon: Zap },
    { label: 'Ownership', value: car.ownership, icon: Car },
    { label: 'Location', value: `${car.locationCity}, ${car.locationState}`, icon: Car },
  ]

  const featureList: string[] = Array.isArray(car.features) ? car.features : []

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-12 py-6">
      <MarketplaceBreadcrumb
        items={[
          { label: 'Cars Marketplace', href: appRoutes.cars },
          { label: car.title },
        ]}
      />

      {/* Main Grid: Gallery & Details vs Sticky Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Left 2 Columns: Gallery, Overview & Specs */}
        <div className="lg:col-span-2 space-y-10">
          {/* Gallery */}
          <ImageGallery images={galleryImages} title={car.title} />

          {/* Title & Price Header */}
          <div className="space-y-4 border-b border-border pb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary/20 text-primary border border-primary/30">
                    {car.condition}
                  </Badge>
                  {car.user?.role === 'DEALER' && (
                    <Badge className="bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                      <ShieldCheck className="h-3 w-3 mr-1" /> Verified Listing
                    </Badge>
                  )}
                </div>
                <h1 className="font-display text-2xl sm:text-4xl font-extrabold text-foreground">{car.title}</h1>
                <p className="text-sm text-muted-foreground">{car.brand?.name} {car.model?.name} {car.variant?.name ? `(${car.variant.name})` : ''}</p>
              </div>

              {/* Price & Actions */}
              <div className="space-y-2 text-left sm:text-right">
                <span className="font-display text-3xl sm:text-4xl font-extrabold text-foreground">{formattedPrice}</span>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{car.negotiable ? 'Negotiable Price' : 'Fixed Price'}</p>

                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" variant="outline" className="border-border text-foreground hover:bg-accent font-semibold">
                    <Heart className="h-4 w-4 mr-1" /> Wishlist
                  </Button>
                  <Button size="sm" variant="outline" className="border-border text-foreground hover:bg-accent font-semibold">
                    <Share2 className="h-4 w-4 mr-1" /> Share
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications Grid */}
          <div className="space-y-4">
            <h3 className="font-display text-xl font-bold text-foreground">Vehicle Specifications</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {specsList.map((spec, i) => (
                <Card key={i} className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-1">
                  <div className="flex items-center gap-2 text-xs text-primary">
                    <spec.icon className="h-4 w-4" />
                    <span className="uppercase text-[10px] tracking-wider text-muted-foreground font-semibold">
                      {spec.label}
                    </span>
                  </div>
                  <div className="font-display text-sm font-bold text-foreground truncate">{String(spec.value)}</div>
                </Card>
              ))}
            </div>
          </div>

          {/* Description */}
          {car.description && (
            <div className="space-y-3 border-t border-border pt-6">
              <h3 className="font-display text-xl font-bold text-foreground">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{car.description}</p>
            </div>
          )}

          {/* Key Features */}
          {featureList.length > 0 && (
            <div className="space-y-4 border-t border-border pt-6">
              <h3 className="font-display text-xl font-bold text-foreground">Installed Options &amp; Packages</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {featureList.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Sticky Contact & Seller Cards */}
        <div className="space-y-6 lg:sticky lg:top-24">
          <SellerCard seller={sellerInfo} onOpenChat={() => setIsChatOpen(true)} />
          <ContactCard title={car.title} onOpenChat={() => setIsChatOpen(true)} />
        </div>
      </div>

      {/* Floating Chat Quick Action Pill Button on Mobile / Desktop */}
      {!isChatOpen && (
        <aside aria-label="Live Vehicle Chat Action" className="fixed bottom-6 right-6 z-40">
          <Button
            size="lg"
            onClick={() => setIsChatOpen(true)}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xl glow-md px-5 py-6 gap-2.5 font-bold border border-white/20 transition-all hover:scale-105"
          >
            <span className="relative flex size-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full size-3 bg-emerald-400"></span>
            </span>
            <MessageSquare className="size-5" />
            <span>Chat with Dealer</span>
          </Button>
        </aside>
      )}

      {/* Interactive Car Chat Drawer */}
      <CarChatDrawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        car={car}
        seller={sellerInfo}
      />

      {/* Similar Cars Interactive Slider */}
      {relatedCars.length > 0 && (
        <div className="border-t border-border pt-12 space-y-6">
          <div className="flex items-end justify-between">
            <SectionHeading
              eyebrow="Marketplace Recommendations"
              title="More Published Luxury Cars"
              description="Explore comparable verified vehicle listings live in stock."
            />

            {/* Slider Navigation Buttons */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="size-9 p-0 rounded-full border-border hover:bg-accent text-foreground"
                onClick={() => scrollSlider('left')}
                title="Previous cars"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="size-9 p-0 rounded-full border-border hover:bg-accent text-foreground"
                onClick={() => scrollSlider('right')}
                title="Next cars"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>

          {/* Smooth Horizontal Scroll Slider */}
          <div
            ref={sliderRef}
            className="flex items-stretch gap-6 overflow-x-auto scrollbar-none pb-4 pt-1 scroll-smooth snap-x"
          >
            {relatedCars.map((c: any) => (
              <div key={c.id} className="min-w-[300px] max-w-[340px] shrink-0 snap-start">
                <CarCard car={c} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
